import { LiaRupeeSignSolid } from "react-icons/lia";
import { useState, useEffect, useMemo } from "react";

import {
  FaCheckCircle,
  FaShippingFast,
  FaBoxOpen,
  FaGem,
  FaGlobe,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";

import { useGetCart } from "../../../queries/useCartQuery";

import {
  Button,
  HeadingTitle,
  Loader,
  Ratings,
  SubHeadingTitle,
} from "../../../components/shared/index";

import styles from "./productdetails.module.css";

import toast from "react-hot-toast";
import authService from "../../../services/authService";
import { useNavigate } from "react-router-dom";

const ProductInfo = ({
  productData,
  addToCart,
  isPending,
  isCartPending,
  isError,
  error,
}) => {
  const isLoggedIn = authService.isLoggedIn();
  const navigate = useNavigate();

  const { data: cartData } = useGetCart();

  const [quantity, setQuantity] = useState(1);

  // =========================================================
  // ALL VARIANTS
  // =========================================================

  const variants = productData?.variants || [];

  // =========================================================
  // UNIQUE SIZES
  // =========================================================

  const sizes = useMemo(() => {
    return [
      ...new Set(
        variants
          .map((variant) => variant?.variant_size)
          .filter(Boolean)
      ),
    ];
  }, [variants]);

  // =========================================================
  // UNIQUE COLORS
  // =========================================================

  const colors = useMemo(() => {
    return [
      ...new Set(
        variants
          .map((variant) => variant?.color_name)
          .filter(Boolean)
      ),
    ];
  }, [variants]);
const formatColorName = (color) => {
  if (!color) return "";

  return color.charAt(0).toUpperCase() + color.slice(1).toLowerCase();
};
  // =========================================================
  // SELECTED SIZE
  // =========================================================

  const [selectedSize, setSelectedSize] = useState("");

  // =========================================================
  // SELECTED COLOR
  // =========================================================

  const [selectedColor, setSelectedColor] = useState("");

  // =========================================================
  // RESET SELECTION WHEN PRODUCT CHANGES
  // =========================================================

  useEffect(() => {
    setSelectedSize("");
    setSelectedColor("");
    setQuantity(1);
  }, [productData?.id]);

  // =========================================================
  // CHECK WHETHER SIZE IS AVAILABLE
  // =========================================================

  const isSizeAvailable = (size) => {
    return variants.some(
      (variant) =>
        variant?.variant_size === size &&
        Number(variant?.variant_qty || 0) > 0
    );
  };

  // =========================================================
  // CHECK WHETHER COLOR IS AVAILABLE
  //
  // IMPORTANT:
  //
  // If size is selected:
  // only check colors available for that size.
  //
  // If size is not selected:
  // show colors that have stock anywhere.
  // =========================================================

  const isColorAvailable = (color) => {
    if (selectedSize) {
      return variants.some(
        (variant) =>
          variant?.variant_size === selectedSize &&
          variant?.color_name === color &&
          Number(variant?.variant_qty || 0) > 0
      );
    }

    return variants.some(
      (variant) =>
        variant?.color_name === color &&
        Number(variant?.variant_qty || 0) > 0
    );
  };

  // =========================================================
  // FIND SELECTED VARIANT
  // =========================================================

  const selectedVariant = useMemo(() => {
    if (!variants.length) {
      return null;
    }

    // -------------------------------------------------------
    // BOTH SIZE + COLOR SELECTED
    // -------------------------------------------------------

    if (selectedSize && selectedColor) {
      return (
        variants.find(
          (variant) =>
            variant?.variant_size === selectedSize &&
            variant?.color_name === selectedColor
        ) || null
      );
    }

    // -------------------------------------------------------
    // ONLY SIZE SELECTED
    // -------------------------------------------------------

    if (selectedSize && !selectedColor) {
      return (
        variants.find(
          (variant) =>
            variant?.variant_size === selectedSize &&
            Number(variant?.variant_qty || 0) > 0
        ) || null
      );
    }

    // -------------------------------------------------------
    // ONLY COLOR SELECTED
    // -------------------------------------------------------

    if (!selectedSize && selectedColor) {
      return (
        variants.find(
          (variant) =>
            variant?.color_name === selectedColor &&
            Number(variant?.variant_qty || 0) > 0
        ) || null
      );
    }

    // -------------------------------------------------------
    // NOTHING SELECTED
    // -------------------------------------------------------

    return null;
  }, [
    variants,
    selectedSize,
    selectedColor,
  ]);

  // =========================================================
  // CHECK WHETHER VARIANT SELECTION IS REQUIRED
  // =========================================================

  const sizeRequired = sizes.length > 0;
  const colorRequired = colors.length > 0;

  const isVariantSelectionComplete =
    (!sizeRequired || !!selectedSize) &&
    (!colorRequired || !!selectedColor);

  // =========================================================
  // PRICE
  // =========================================================

const productPrice = Number(
  selectedVariant?.variant_price ??
    productData?.price ??
    productData?.regular_price ??
    productData?.original_price ??
    productData?.base_price ??
    productData?.variants?.[0]?.variant_price ??
    0
);

  // =========================================================
  // OFFER PRICE
  // =========================================================

  
  const offerPrice =
  selectedVariant?.variant_offer_price !== null &&
  selectedVariant?.variant_offer_price !== undefined &&
  selectedVariant?.variant_offer_price !== ""
    ? Number(selectedVariant.variant_offer_price)
    : Number(
        productData?.offer_price ??
          productData?.selling_price ??
          productData?.sale_price ??
          productData?.variants?.[0]?.variant_offer_price ??
          0
      );

  // =========================================================
  // AVAILABLE QUANTITY
  // =========================================================

  const availableQty = selectedVariant
    ? Number(selectedVariant?.variant_qty ?? 0)
    : 0;

  // =========================================================
  // SKU
  // =========================================================

  const sku =
    selectedVariant?.variant_sku || "N/A";

  // =========================================================
  // OFFER CHECK
  // =========================================================

  const hasOffer =
    offerPrice > 0 &&
    offerPrice < productPrice;

  // =========================================================
  // CART CHECK
  // =========================================================

  const isAlreadyInCart = cartData?.some(
    (item) =>
      item.product_id === productData?.id &&
      (
        !selectedVariant ||
        item.product_variant_item_id ===
          selectedVariant?.id
      )
  );

  // =========================================================
  // RESET QUANTITY WHEN VARIANT CHANGES
  // =========================================================

  useEffect(() => {
    setQuantity(1);
  }, [selectedVariant?.id]);

  // =========================================================
  // SIZE CHANGE
  // =========================================================

  const handleSizeChange = (size) => {
    setSelectedSize(size);

    // Find colors available for this size
    const matchingVariants = variants.filter(
      (variant) =>
        variant?.variant_size === size &&
        Number(variant?.variant_qty || 0) > 0
    );

    // Keep current color only if it exists
    // for the selected size and has stock.
    const currentColorStillAvailable =
      matchingVariants.some(
        (variant) =>
          variant?.color_name === selectedColor
      );

    if (!currentColorStillAvailable) {
      setSelectedColor("");
    }
  };

  // =========================================================
  // COLOR CHANGE
  // =========================================================

  const handleColorChange = (color) => {
    setSelectedColor(color);

    // Find sizes available for this color
    const matchingVariants = variants.filter(
      (variant) =>
        variant?.color_name === color &&
        Number(variant?.variant_qty || 0) > 0
    );

    // Keep current size only if it exists
    // for the selected color and has stock.
    const currentSizeStillAvailable =
      matchingVariants.some(
        (variant) =>
          variant?.variant_size === selectedSize
      );

    if (!currentSizeStillAvailable) {
      setSelectedSize("");
    }
  };

  // =========================================================
  // ADD TO CART
  // =========================================================

  const addItemToCart = async () => {
    if (!isLoggedIn) {
      toast.error("⚠️ Please login first");
      return false;
    }

    if (!isVariantSelectionComplete) {
      toast.error("Please select size and color");
      return false;
    }

    if (!selectedVariant) {
      toast.error("Please select a valid variant");
      return false;
    }

    if (availableQty < 1) {
      toast.error("❌ Selected variant is out of stock");
      return false;
    }

    try {
      await addToCart({
        product_id: productData?.id,

        // IMPORTANT:
        // Selected variant ID
        product_variant_item_id:
          selectedVariant?.id,

        quantity: quantity,
      });

      toast.success("Item added to cart");

      return true;
    } catch {
      toast.error("Something went wrong");
      return false;
    }
  };

  // =========================================================
  // ADD TO CART BUTTON
  // =========================================================

  const handleAddToCart = async () => {
    if (!isVariantSelectionComplete) {
      toast.error("Please select size and color");
      return;
    }

    if (isAlreadyInCart) {
      toast.error("Selected variant already added to cart");
      return;
    }

    await addItemToCart();
  };

  // =========================================================
  // BUY NOW
  // =========================================================

  const handleBuyNow = () => {
    if (!isVariantSelectionComplete) {
      toast.error("Please select size and color");
      return;
    }

    if (!selectedVariant) {
      toast.error("Please select a valid variant");
      return;
    }

    if (availableQty < 1) {
      toast.error("❌ Selected variant is out of stock");
      return;
    }

    navigate("/checkout/payment", {
      state: {
        buyNow: true,
        product: productData,

        // Send complete selected variant
        selectedVariant: selectedVariant,

        // Send selected options
        selectedSize: selectedSize,
        selectedColor: selectedColor,

        quantity: quantity,
      },
    });
  };

  // =========================================================
  // QUANTITY
  // =========================================================

  const increaseQty = () => {
    if (quantity < availableQty) {
      setQuantity((prev) => prev + 1);
    }
  };

  const decreaseQty = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  console.log("Selected Size:", selectedSize);
  console.log("Selected Color:", selectedColor);
  console.log("Selected Variant:", selectedVariant);

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className={styles.rightSection}>
      {isPending ? (
        <div className={styles.productInfoLoader}>
          <Loader title="Loading product details..." />
        </div>
      ) : isError ? (
        <SubHeadingTitle
          style={styles.errorMessage}
          subTitle={
            error?.message ||
            "Failed to load product details"
          }
        />
      ) : (
        <>
          {/* =================================================
              PRODUCT NAME
          ================================================= */}

          <HeadingTitle
            text={productData?.name || ""}
            style={styles.heading}
          />

          {/* =================================================
              RATINGS
          ================================================= */}

          <Ratings
            rating={
              productData?.rating ||
              productData?.reviews_avg_rating ||
              0
            }
          />

          <div className={styles.metaInfo}>

            {/* =================================================
                SIZE
            ================================================= */}

            {sizes.length > 0 && (
              <div className={styles.variantSection}>

                <div className={styles.variantTitle}>
                  <span>Select Size</span>

                  {selectedSize && (
                    <strong>
                      : {selectedSize}
                    </strong>
                  )}
                </div>

                <div className={styles.variantOptions}>
                  {sizes.map((size) => {
                    const available =
                      isSizeAvailable(size);

                    return (
                      <button
                        key={size}
                        type="button"
                        className={`
                          ${styles.variantButton}
                          ${
                            selectedSize === size
                              ? styles.selectedVariant
                              : ""
                          }
                          ${
                            !available
                              ? styles.disabledVariant
                              : ""
                          }
                        `}
                        disabled={!available}
                        onClick={() =>
                          handleSizeChange(size)
                        }
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* =================================================
                COLOR
            ================================================= */}

            {colors.length > 0 && (
              <div className={styles.variantSection}>

                <div className={styles.variantTitle}>
                  <span>Select Color</span>

                  {selectedColor && (
                    <strong>
                      : {selectedColor}
                    </strong>
                  )}
                </div>

                <div className={styles.variantOptions}>
                  {colors.map((color) => {
                    const available =
                      isColorAvailable(color);

                    return (
                      <button
                        key={color}
                        type="button"
                        className={`
                          ${styles.variantButton}
                          ${styles.colorButton}
                          ${
                            selectedColor === color
                              ? styles.selectedVariant
                              : ""
                          }
                          ${
                            !available
                              ? styles.disabledVariant
                              : ""
                          }
                        `}
                        disabled={!available}
                        onClick={() =>
                          handleColorChange(color)
                        }
                      >
                        {!available && (
                        <span
  className={
    !available
      ? styles.unavailableColorText
      : ""
  }
>
  
</span>
                        )}

                      {formatColorName(color)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* =================================================
                PLEASE SELECT MESSAGE
            ================================================= */}

            {!isVariantSelectionComplete &&
              (sizeRequired || colorRequired) && (
                <div
                  className={
                    styles.selectVariantMessage
                  }
                >
                  Please select
                  {sizeRequired
                    ? " size"
                    : ""}
                  {sizeRequired && colorRequired
                    ? " and"
                    : ""}
                  {colorRequired
                    ? " color"
                    : ""}{" "}
                  to continue
                </div>
              )}

            {/* =================================================
                PRICE
            ================================================= */}

            <div className={styles.priceWrapper}>
              {hasOffer ? (
                <>
                  <h3
                    className={
                      styles.offerPrice
                    }
                  >
                    <LiaRupeeSignSolid
                      className={
                        styles.priceIcon
                      }
                      size={24}
                    />

                    {offerPrice.toLocaleString(
                      "en-IN"
                    )}{" "}
                    /-
                  </h3>

                  <h4
                    className={
                      styles.originalPrice
                    }
                  >
                    <LiaRupeeSignSolid
                      className={
                        styles.priceIcon
                      }
                      size={18}
                    />

                    {productPrice.toLocaleString(
                      "en-IN"
                    )}
                  </h4>

                  {productPrice > 0 && (
                    <span
                      className={
                        styles.discountText
                      }
                    >
                      {Math.round(
                        ((productPrice -
                          offerPrice) /
                          productPrice) *
                          100
                      )}
                      % off
                    </span>
                  )}
                </>
              ) : (
                <h3
                  className={
                    styles.offerPrice
                  }
                >
                  <LiaRupeeSignSolid
                    className={
                      styles.priceIcon
                    }
                    size={24}
                  />

                  {productPrice.toLocaleString(
                    "en-IN"
                  )}{" "}
                  /-
                </h3>
              )}
            </div>

            {/* =================================================
                SKU
            ================================================= */}

            <SubHeadingTitle
              style={styles.sku}
              subTitle={
                <>
                  <span>SKU : </span>
                  <span>{sku}</span>
                </>
              }
            />
          </div>

          {/* =================================================
              QUANTITY
          ================================================= */}

          {isVariantSelectionComplete &&
            availableQty > 0 && (
              <div className={styles.qtyRow}>
                <span
                  className={styles.qtyTitle}
                >
                  Quantity :
                </span>

                <div className={styles.qtyBox}>
                  <span
                    className={
                      styles.qtyNumber
                    }
                  >
                    {quantity}
                  </span>

                  <div
                    className={
                      styles.qtyArrows
                    }
                  >
                    <button
                      type="button"
                      onClick={increaseQty}
                      disabled={
                        quantity >=
                        availableQty
                      }
                    >
                      <FaChevronUp
                        size={12}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={decreaseQty}
                      disabled={
                        quantity === 1
                      }
                    >
                      <FaChevronDown
                        size={12}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

          {/* =================================================
              AVAILABILITY
          ================================================= */}

          <SubHeadingTitle
            subTitle={
              <>
                <span>Availability: </span>

                {!isVariantSelectionComplete ? (
                  <span
                    className={
                      styles.selectAvailability
                    }
                  >
                    Select variant
                  </span>
                ) : (
                  <span
                    className={
                      availableQty > 0
                        ? styles.inStock
                        : styles.outOfStock
                    }
                  >
                    {availableQty > 0
                      ? `In Stock `
                      : "Out of Stock"}
                  </span>
                )}
              </>
            }
          />

          {/* =================================================
              BUTTONS
          ================================================= */}

          <div className={styles.btnGroup}>
            <Button
              type="button"
              title={
                isCartPending
                  ? "Adding to Cart..."
                  : isAlreadyInCart
                  ? "Already in Cart"
                  : "Add To Cart"
              }
              style={styles.cartBtn}
              disabled={
                !isVariantSelectionComplete ||
                availableQty < 1 ||
                isCartPending ||
                isAlreadyInCart
              }
              onHandler={
                handleAddToCart
              }
            />

            <Button
              type="button"
              title="Buy Now"
              style={styles.buyBtn}
              disabled={
                !isVariantSelectionComplete ||
                availableQty < 1 ||
                isCartPending
              }
              onHandler={handleBuyNow}
            />
          </div>

          {/* =================================================
              FEATURES
          ================================================= */}

          <ul className={styles.featureList}>
            <li>
              <FaCheckCircle
                size={22}
                color="black"
              />{" "}
              COD, Easy Returns & Quick
              Exchanges
            </li>

            <li>
              <FaShippingFast
                size={22}
                color="black"
              />{" "}
              Express 24-Hour Delivery
              (Charges Apply)
            </li>

            <li>
              <FaBoxOpen
                size={22}
                color="black"
              />{" "}
              Free Shipping on Prepaid Orders
              Above ₹1000
            </li>

            <li>
              <FaGem
                size={22}
                color="black"
              />{" "}
              Premium Quality Stones, Plating
              & Handwork
            </li>

            <li>
              <FaGlobe
                size={22}
                color="black"
              />{" "}
              Trusted by 1.2M+ Customers
              Worldwide
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

export default ProductInfo;